import { useState } from 'react';
import { DEFAULT_FILTERS, MAX_PRICE_OPTIONS } from '../FiltersModal';
import { calculateOriginalPrice } from '../../../utils/priceComparison';

const RANGE_SHEET_CONFIG = {
  maxPrice: { title: 'Por menos de', options: MAX_PRICE_OPTIONS },
};

/**
 * Estado de los filtros del menú (pills + modal + range sheet).
 * Single-restaurant: filtran PRODUCTOS, no negocios.
 */
export function useMenuFilters() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [rangeSheetKey, setRangeSheetKey] = useState(null);

  const setFilterValue = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const openFiltersModal = () => setFiltersModalOpen(true);
  const closeFiltersModal = () => setFiltersModalOpen(false);
  const openRangeSheet = (key) => setRangeSheetKey(key);
  const closeRangeSheet = () => setRangeSheetKey(null);

  const activeRangeSheet = rangeSheetKey ? RANGE_SHEET_CONFIG[rangeSheetKey] : null;

  return {
    filters,
    setFilters,
    setFilterValue,
    filtersActive: hasActiveFilters(filters),
    filtersModalOpen,
    openFiltersModal,
    closeFiltersModal,
    rangeSheetKey,
    activeRangeSheet,
    openRangeSheet,
    closeRangeSheet,
  };
}

// true si el usuario tocó algún filtro (sirve para el empty state y para
// ocultar la sección de Populares mientras se filtra).
export function hasActiveFilters(filters) {
  return (
    filters.maxPrice != null ||
    filters.onlyBestseller ||
    filters.onlyDiscount ||
    (filters.sortBy && filters.sortBy !== 'relevance')
  );
}

/**
 * Aplica los filtros al menú (categorías con sus productos). Pura: devuelve un
 * nuevo array. Las categorías que quedan sin productos se descartan, así los
 * chips y las secciones muestran siempre lo mismo.
 */
export function applyMenuFilters(categories, filters) {
  if (!hasActiveFilters(filters)) return categories;

  const { maxPrice, onlyBestseller, onlyDiscount, sortBy } = filters;

  return categories
    .map((cat) => {
      let products = cat.products.filter((p) => {
        if (maxPrice != null && !(p.price <= maxPrice)) return false;
        // Más vendidos = los marcados como Bestseller en el dashboard.
        if (onlyBestseller && !p.isBestseller) return false;
        // El descuento es determinístico por id (ver utils/priceComparison):
        // null = ese producto no muestra precio tachado.
        if (onlyDiscount && !calculateOriginalPrice(p.price, p._id || p.id)) return false;
        return true;
      });

      if (sortBy === 'priceAsc') {
        products = [...products].sort((a, b) => (a.price ?? 0) - (b.price ?? 0));
      } else if (sortBy === 'priceDesc') {
        products = [...products].sort((a, b) => (b.price ?? 0) - (a.price ?? 0));
      }

      return { ...cat, products };
    })
    .filter((cat) => cat.products.length > 0);
}
